import { useState, useEffect } from 'react'
import './LazyImage.css'

const DEFAULT_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='

const LazyImage = ({ 
  src, 
  alt, 
  fallbackSrc, 
  className = '', 
  placeholder = null,
  showLoadingIndicator = true,
  ...props 
}) => {
  const placeholderSrc = placeholder || DEFAULT_PLACEHOLDER
  const hasCustomPlaceholder = Boolean(placeholder)
  const [imageSrc, setImageSrc] = useState(placeholderSrc)
  const [imageRef, setImageRef] = useState()
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    setImageSrc(placeholderSrc)
    setLoaded(false)
    setError(false)
  }, [src, placeholderSrc])

  useEffect(() => {
    let observer
    
    if (imageRef && imageSrc === placeholderSrc) {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setLoaded(false)
              setImageSrc(src)
              observer.unobserve(imageRef)
            }
          })
        },
        { threshold: 0.1 }
      )
      observer.observe(imageRef)
    }
    
    return () => {
      if (observer && observer.unobserve && imageRef) {
        observer.unobserve(imageRef)
      }
    }
  }, [imageRef, imageSrc, src, placeholderSrc])

  const handleLoad = () => {
    setLoaded(true)
    setError(false)
  }

  const handleError = () => {
    setLoaded(false)

    if (fallbackSrc && imageSrc !== fallbackSrc) {
      setError(false)
      setImageSrc(fallbackSrc)
      return
    }

    if (hasCustomPlaceholder) {
      setError(false)
      setLoaded(true)
      setImageSrc(placeholderSrc)
      return
    }

    setError(true)
  }

  return (
    <div
      className={`lazy-image-container ${hasCustomPlaceholder ? 'has-custom-placeholder' : ''} ${className}`}
      style={hasCustomPlaceholder ? { '--lazy-placeholder': `url("${placeholderSrc}")` } : undefined}
    >
      <img
        ref={setImageRef}
        src={imageSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={`lazy-image ${loaded ? 'loaded' : ''} ${error ? 'error' : ''}`}
        {...props}
      />
      {showLoadingIndicator && !loaded && !error && (
        <div className="lazy-image-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  )
}

export default LazyImage
